export type HermesMediaCandidate = {
  id: number;
  slug: string;
  kind: "image" | "video" | "audio";
  name?: string | null;
  notes?: string | null;
};

export type HermesMediaCandidatesPayload = {
  items: HermesMediaCandidate[];
  totalCount: number;
  truncated: boolean;
  viewAllScope: "enterprise" | "brand";
};

function coercePositiveInt(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    const n = Number(value.trim());
    return n > 0 ? n : null;
  }
  return null;
}

function parseCandidate(entry: unknown): HermesMediaCandidate | null {
  if (!entry || typeof entry !== "object") return null;
  const record = entry as Record<string, unknown>;
  const id = coercePositiveInt(record.id);
  if (id == null) return null;
  if (typeof record.slug !== "string" || record.slug.trim() === "") return null;
  if (record.kind !== "image" && record.kind !== "video" && record.kind !== "audio") return null;

  const candidate: HermesMediaCandidate = {
    id,
    slug: record.slug.trim(),
    kind: record.kind,
  };
  if (record.name === null || typeof record.name === "string") {
    candidate.name = record.name;
  }
  if (record.notes === null || typeof record.notes === "string") {
    candidate.notes = record.notes;
  }
  return candidate;
}

export function hermesMessageMediaCandidatesFromPayload(
  toolPayload: Record<string, unknown> | null | undefined,
): HermesMediaCandidatesPayload | null {
  if (!toolPayload || typeof toolPayload.media_candidates !== "object" || !toolPayload.media_candidates) {
    return null;
  }

  const mediaCandidates = toolPayload.media_candidates as Record<string, unknown>;
  if (!Array.isArray(mediaCandidates.items)) return null;
  const totalCount = coercePositiveInt(mediaCandidates.total_count);
  if (totalCount == null) return null;
  if (typeof mediaCandidates.truncated !== "boolean") return null;

  const viewAll = mediaCandidates.view_all;
  if (!viewAll || typeof viewAll !== "object") return null;
  const scope = (viewAll as Record<string, unknown>).scope;
  if (scope !== "enterprise" && scope !== "brand") return null;

  const items: HermesMediaCandidate[] = [];
  const seen = new Set<string>();
  for (const entry of mediaCandidates.items) {
    const candidate = parseCandidate(entry);
    if (!candidate) continue;
    if (seen.has(candidate.slug)) continue;
    seen.add(candidate.slug);
    items.push(candidate);
  }

  return {
    items,
    totalCount,
    truncated: mediaCandidates.truncated,
    viewAllScope: scope,
  };
}
